import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import DataTable from './Common/DataTable';
import Modal from './Common/Modal';
import { CURRENCY } from '../config';

const FEE_TYPES = ['Tuition', 'Transport', 'Library', 'Exam', 'Admission', 'Other'];

export default function FeeStructureManager() {
  const [structures, setStructures] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    class_id: '',
    fee_type: 'Tuition',
    amount: 0,
    late_fee: 0,
    academic_year: '2025-2026',
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get('/fees/structures'),
        api.get('/classes/'),
      ]);
      setStructures(s.data);
      setClasses(c.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        class_id: parseInt(form.class_id),
        amount: parseFloat(form.amount),
        late_fee: parseFloat(form.late_fee) || 0,
      };
      if (editing) {
        await api.put(`/fees/structures/${editing.id}`, {
          amount: payload.amount,
          late_fee: payload.late_fee,
          academic_year: payload.academic_year,
        });
      } else {
        await api.post('/fees/structures', payload);
      }
      setModalOpen(false);
      setEditing(null);
      setForm({
        class_id: '',
        fee_type: 'Tuition',
        amount: 0,
        late_fee: 0,
        academic_year: '2025-2026',
      });
      fetchData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this fee structure?')) return;
    try {
      await api.delete(`/fees/structures/${id}`);
      fetchData();
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  };

  const getClassName = (id) => {
    const c = classes.find(x => x.id === id);
    return c ? c.name : '—';
  };

  const columns = ['Class', 'Fee Type', 'Base Amount', 'Late Fee', 'Academic Year', 'Actions'];
  const data = structures.map(s => ({
    Class: getClassName(s.class_id),
    'Fee Type': s.fee_type,
    'Base Amount': `${CURRENCY}${s.amount.toLocaleString()}`,
    'Late Fee': s.late_fee > 0 ? (
      <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">
        {CURRENCY}{s.late_fee.toLocaleString()}
      </span>
    ) : (
      <span className="text-xs text-gray-400">—</span>
    ),
    'Academic Year': s.academic_year || '—',
    Actions: (
      <div className="flex gap-2">
        <button
          onClick={() => {
            setEditing(s);
            setForm({
              class_id: s.class_id,
              fee_type: s.fee_type,
              amount: s.amount,
              late_fee: s.late_fee || 0,
              academic_year: s.academic_year || '',
            });
            setModalOpen(true);
          }}
          className="text-blue-600 hover:text-blue-800"
        >
          <i className="fas fa-edit"></i>
        </button>
        <button
          onClick={() => handleDelete(s.id)}
          className="text-red-600 hover:text-red-800"
        >
          <i className="fas fa-trash"></i>
        </button>
      </div>
    ),
  }));

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold">🏷️ Fee Structure (per Class)</h2>
        <button
          onClick={() => {
            setEditing(null);
            setForm({
              class_id: '',
              fee_type: 'Tuition',
              amount: 0,
              late_fee: 0,
              academic_year: '2025-2026',
            });
            setModalOpen(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          <i className="fas fa-plus mr-1"></i> Add Fee Structure
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 p-3 rounded mb-4 text-sm text-blue-700">
        <i className="fas fa-info-circle mr-1"></i>
        Define the base fee per class. When you auto-generate monthly fees, each student is
        charged based on their class's structure — then any student-level discount (set on
        the student profile) is applied automatically.
        <br />
        <strong>Late Fee</strong>: if set, the accountant can apply this charge when a fee
        becomes overdue.
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : data.length === 0 ? (
        <div className="bg-white p-8 rounded shadow text-center text-gray-400">
          <i className="fas fa-inbox text-4xl mb-3"></i>
          <p>No fee structures yet. Click "Add Fee Structure" to create one.</p>
        </div>
      ) : (
        <div className="bg-white p-4 rounded shadow">
          <DataTable columns={columns} data={data} />
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        title={editing ? 'Edit Fee Structure' : 'Add Fee Structure'}
        onSave={handleSubmit}
        saveLabel={editing ? 'Update' : 'Create'}
      >
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Class</label>
            <select
              className="w-full p-2 border rounded"
              value={form.class_id}
              onChange={(e) => setForm({ ...form, class_id: e.target.value })}
              required
              disabled={!!editing}
            >
              <option value="">— Select a class —</option>
              {classes.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Fee Type</label>
            <select
              className="w-full p-2 border rounded"
              value={form.fee_type}
              onChange={(e) => setForm({ ...form, fee_type: e.target.value })}
              disabled={!!editing}
            >
              {FEE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                Base Amount ({CURRENCY.trim()})
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full p-2 border rounded"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Late Fee ({CURRENCY.trim()})
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full p-2 border rounded"
                value={form.late_fee}
                onChange={(e) => setForm({ ...form, late_fee: e.target.value })}
                placeholder="0"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Applied manually when a fee is overdue. Leave 0 to disable.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Academic Year</label>
            <input
              className="w-full p-2 border rounded"
              placeholder="2025-2026"
              value={form.academic_year}
              onChange={(e) => setForm({ ...form, academic_year: e.target.value })}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}